/* eslint-disable no-await-in-loop */
/* eslint-disable no-use-before-define */
import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { mobileFormFieldsTypes, textfieldTypes } from '../../../config/constants/mobileFormConstant';
import { AssetsService } from '../../../services/assetsService';

const useMobileFormValues = ({ values, defaultValues = {} }) => {
  const [mobileFormValues, setMobileFormValues] = useState(defaultValues);
  const [isLoading, setIsLoading] = useState(false);
  const valuesToSaveRef = useRef(mobileFormValues);

  const updateFieldValue = useCallback(({ idInput, value }) => {
    valuesToSaveRef.current = { ...valuesToSaveRef.current, [idInput]: value };
  }, []);

  const prepareRadiosValue = async (radiosField) => {
    const radioItem = radiosField.value?.find((item) => item.value);
    if (!radioItem) return null;
    const radioItemValue = { ...radioItem };
    if (radioItem.valueConditional != null) {
      radioItemValue.valueConditional = await prepareValuesPage(radioItem.valueConditional);
    }
    return radioItemValue;
  };

  const prepareLoopValue = (loopField) => {
    const loopItems = loopField.value;
    if (!loopItems?.length) return null;
    const loopValue = loopItems.map((loopItem) => prepareValuesPage(loopItem[0].fields));
    return Promise.all(loopValue);
  };

  const prepareAssetValue = async (assetField) => {
    if (!assetField.value) return null;
    const { asset } = await AssetsService.getAssetById(assetField.value);
    return asset;
  };

  const prepareCheckboxGroupValue = async (checkboxField) => {
    if (!checkboxField.value) return null;
    return checkboxField.value.reduce((acc, item) => {
      acc[item.idInput] = item.value;
      return acc;
    }, {});
  };

  const prepareSimpleListValue = async (listField) => {
    if (!listField.value) return null;
    const listNodes = [];
    let currentNodes = listNodes;
    listField.value.split?.(' > ').forEach((item) => {
      const newNode = {
        id: item,
        title: item,
        nodes: [],
        checked: true,
      };
      currentNodes.push(newNode);
      currentNodes = newNode.nodes;
    });
    return listNodes.length ? listNodes[0] : { nodes: listNodes };
  };

  const prepareMultipleListValue = async (listField) => {
    if (!listField.value) return null;
    const nodes = {};
    let currentNodes = nodes;
    for (const listItemPath of listField.value) {
      for (let i = 0; i < listItemPath.length; i += 1) {
        const nodeName = listItemPath[i];
        currentNodes[nodeName] ??= {};
        currentNodes = currentNodes[nodeName];
      }
      currentNodes = nodes;
    }
    const reduceItems = (nodesMap) => {
      return Object.keys(nodesMap).map((itemName) => {
        return {
          id: itemName,
          title: itemName,
          nodes: reduceItems(nodesMap[itemName]),
          checked: true,
        };
      });
    };
    const listNodes = reduceItems(nodes);
    return listNodes.length ? listNodes[0] : { nodes: listNodes };
  };

  const prepareDatetimeValue = (field) => {
    if (!field.value) return null;
    return moment(field.value, 'MMM D, YYYY h:mm A').toDate();
  };

  const prepareDateValue = (field) => {
    if (!field.value) return null;
    return moment(field.value, 'MMM D, YYYY').toDate();
  };

  const prepareTextValue = (field) => {
    const handlers = {
      [textfieldTypes.DATE.name]: prepareDateValue,
      [textfieldTypes.DATETIME.name]: prepareDatetimeValue,
      default: (item) => item.value,
    };
    const handler = handlers[field.typeTextField] ?? handlers.default;
    return handler(field);
  };

  const prepareValuesPage = async (fields, valuesMap = {}) => {
    const handlers = {
      [mobileFormFieldsTypes.text.value]: prepareTextValue,
      [mobileFormFieldsTypes.radios.value]: prepareRadiosValue,
      [mobileFormFieldsTypes.loop.value]: prepareLoopValue,
      [mobileFormFieldsTypes.asset.value]: prepareAssetValue,
      [mobileFormFieldsTypes.checkboxGroup.value]: prepareCheckboxGroupValue,
      [mobileFormFieldsTypes.list.value]: prepareSimpleListValue,
      [mobileFormFieldsTypes.listMultiple.value]: prepareMultipleListValue,
      default: (field) => field.value,
    };
    for (const field of fields) {
      const handler = handlers[field.type] ?? handlers.default;
      valuesMap[field.idInput] = await handler(field);
    }
    return valuesMap;
  };

  const prepareValues = async (formValues) => {
    const valuesMap = {};
    await Promise.allSettled(formValues.map((page) => prepareValuesPage(page.fields, valuesMap)));
    return valuesMap;
  };

  const copyValues = (formValues) => {
    if (Array.isArray(formValues)) {
      return formValues.map(copyValues);
    }
    if (formValues instanceof File || formValues instanceof Blob) {
      return formValues;
    }
    if (typeof formValues === 'object') {
      const newValues = {};
      Object.entries(formValues).forEach(([key, value]) => {
        if (value?.valueConditional) {
          value.valueConditional = copyValues(value?.valueConditional);
        }
        newValues[key] = value;
      });
      return newValues;
    }
    return formValues;
  };

  const getValuesToSave = () => {
    return copyValues(valuesToSaveRef.current);
  };

  const updateMobileFormValues = (newValues = {}) => {
    setMobileFormValues(newValues);
    valuesToSaveRef.current = newValues;
  };

  useEffect(() => {
    if (!values) {
      setIsLoading(false);
      return;
    }
    const setupValues = async () => {
      const formValues = await prepareValues(values);
      setMobileFormValues(formValues);
      valuesToSaveRef.current = formValues;
      setIsLoading(false);
    };
    setupValues();
  }, [values]);

  return { mobileFormValues, updateFieldValue, updateMobileFormValues, getValuesToSave, isLoading };
};

export default useMobileFormValues;
